/* eslint-disable react/forbid-dom-props */
import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Download, CheckCircle, FileText, Building2,
  MapPin, ChevronLeft, X, AlertTriangle, Clock, Cpu,
} from 'lucide-react';
import { casesApi, petitionsApi } from '../../services/api';
import { triggerBlobDownload } from '../../utils/download';
import { formatDate } from '../../utils/masks';
import DocumentPreview from '../../components/DocumentPreview';
import QualityPanel from '../../components/QualityPanel';
import CalculoPanel from '../../components/CalculoPanel';
import { useAuthStore } from '../../store/useAuthStore';
import { PERMISSIONS } from '../../rbac/permissions';

const APPROVAL_STATUSES = ['aguardando_aprovacao'];

function downloadFilename(caso, version) {
  const cliente = (caso.client_name || 'cliente').toLowerCase().replace(/[^a-z0-9]+/g, '_');
  return `peticao_v${version || 1}_${cliente}.docx`;
}

function AdjustModal({ onClose, onConfirm, submitting }) {
  const [text, setText] = useState('');
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3 style={{ fontSize: '1.125rem' }}>Solicitar Ajuste de Padronização</h3>
          <button className="btn btn-ghost btn-sm" onClick={onClose} style={{ padding: '.2rem .4rem' }}>
            <X size={16} />
          </button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: '.875rem', marginBottom: '.75rem' }}>
            Descreva o ajuste de padronização necessário antes de aprovar:
          </p>
          <textarea
            value={text}
            onChange={e => setText(e.target.value)}
            rows={5}
            placeholder="Ex: Ajustar o cabeçalho conforme modelo padrão, corrigir numeração dos pedidos..."
            autoFocus
          />
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancelar</button>
          <button
            className="btn btn-gold btn-sm"
            onClick={() => onConfirm(text)}
            disabled={!text.trim() || submitting}
          >
            <AlertTriangle size={13} /> {submitting ? 'Enviando...' : 'Enviar Solicitação'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ConfirmApprovalModal({ caso, onClose, onConfirm, submitting }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 440 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3 style={{ fontSize: '1.125rem' }}>Confirmar Aprovação</h3>
          <button className="btn btn-ghost btn-sm" onClick={onClose} style={{ padding: '.2rem .4rem' }}>
            <X size={16} />
          </button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: '.875rem', marginBottom: '.875rem' }}>
            A petição será marcada como aprovada e o caso seguirá para protocolo.
            Confira o que está sendo aprovado:
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '.45rem', padding: '.875rem 1rem', background: 'var(--bg)', border: '1px solid var(--border-light)', borderRadius: 'var(--radius-md)' }}>
            <InfoRow label="Cliente" value={caso.client_name} />
            <InfoRow label="Reclamado" value={caso.defendant_name} />
            <InfoRow label="Comarca" value={caso.comarca} />
            <InfoRow label="Versão" value={`v${caso.petition_version || 1}`} />
            <InfoRow label="Arquivo" value={downloadFilename(caso, caso.petition_version)} />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={onClose}>Voltar e conferir</button>
          <button
            className="btn btn-primary btn-sm"
            style={{ background: 'var(--success)', borderColor: 'var(--success)' }}
            onClick={onConfirm}
            disabled={submitting}
          >
            <CheckCircle size={13} /> {submitting ? 'Aprovando...' : 'Confirmar aprovação'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ApprovalMode({ caso, onBack, onResolved }) {
  const [observations, setObservations] = useState('');
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [approving, setApproving] = useState(false);
  const [adjusting, setAdjusting] = useState(false);
  const [conferido, setConferido] = useState(false);

  const { data: petition, isLoading: loadingPetition } = useQuery({
    queryKey: ['petition', caso.case_id],
    queryFn: () => petitionsApi.getByCaseId(caso.case_id),
    enabled: !!caso.case_id,
  });

  const { data: versions = [] } = useQuery({
    queryKey: ['petition-versions', caso.case_id],
    queryFn: () => petitionsApi.getVersions(caso.case_id),
    enabled: !!caso.case_id,
  });
  const activePetitionId = caso.petition_id || petition?.id;
  const activePetitionVersion = caso.petition_version || petition?.version || 1;

  async function handleApprove() {
    if (!activePetitionId) {
      toast.error('Petição ainda não encontrada para este caso.');
      return;
    }
    setApproving(true);
    try {
      await petitionsApi.approve(activePetitionId);
      setShowConfirmModal(false);
      toast.success('Petição aprovada e enviada para protocolo.');
      onResolved(caso.case_id);
    } catch {
      toast.error('Erro ao aprovar a petição');
    } finally {
      setApproving(false);
    }
  }

  async function handleAdjustConfirm(text) {
    if (!activePetitionId) {
      toast.error('Petição ainda não encontrada para este caso.');
      return;
    }
    setAdjusting(true);
    try {
      await petitionsApi.reject(activePetitionId, text);
      setShowAdjustModal(false);
      toast.success('Solicitação de ajuste enviada — caso retornado para a ficha.');
      onResolved(caso.case_id);
    } catch {
      toast.error('Erro ao solicitar ajuste');
    } finally {
      setAdjusting(false);
    }
  }

  async function handleDownload(format = 'docx') {
    if (!activePetitionId) {
      toast.error('Petição ainda não encontrada para este caso.');
      return;
    }
    try {
      const blob = await petitionsApi.download(activePetitionId, format);
      const ext = format === 'md' ? 'md' : 'docx';
      const cliente = (caso.client_name || 'cliente').toLowerCase().replace(/[^a-z0-9]+/g, '_');
      triggerBlobDownload(blob, `peticao_v${activePetitionVersion}_${cliente}.${ext}`);
      toast.success(`Download iniciado`);
    } catch {
      toast.error('Erro ao baixar a petição');
    }
  }

  async function handleDownloadVersion(vId, versionNum, format = 'docx') {
    try {
      const blob = await petitionsApi.download(vId, format);
      const ext = format === 'md' ? 'md' : 'docx';
      const cliente = (caso.client_name || 'cliente').toLowerCase().replace(/[^a-z0-9]+/g, '_');
      triggerBlobDownload(blob, `peticao_v${versionNum}_${cliente}.${ext}`);
      toast.success('Download iniciado');
    } catch {
      toast.error('Erro ao baixar a versão selecionada');
    }
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '.75rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <button className="btn btn-ghost btn-sm" onClick={onBack}>
          <ChevronLeft size={14} /> Voltar
        </button>
        <div>
          <h1 style={{ fontSize: '1.375rem' }}>Aprovação Final</h1>
          <p style={{ marginTop: '.1rem', fontSize: '.85rem' }}>
            {caso.client_name} · {caso.defendant_name}
          </p>
        </div>
      </div>

      <div className="revisao-layout">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          {/* Ações de download */}
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{
              background: caso.status === 'aprovada' || caso.status === 'aguardando_protocolo' || caso.status === 'protocolado'
                ? '#e6f4ea'
                : caso.status === 'aguardando_aprovacao'
                ? '#fff7e6'
                : '#e8f0fe',
              color: caso.status === 'aprovada' || caso.status === 'aguardando_protocolo' || caso.status === 'protocolado'
                ? '#137333'
                : caso.status === 'aguardando_aprovacao'
                ? '#b06000'
                : '#1a73e8',
              padding: '.6rem 1.375rem',
              fontSize: '.8rem',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderBottom: '1px solid var(--border-light)'
            }}>
              <span>Estado: {
                caso.status === 'aprovada' || caso.status === 'aguardando_protocolo' ? 'APROVADA (Pronta para protocolo)' :
                caso.status === 'protocolado' ? 'PROTOCOLADA' :
                caso.status === 'aguardando_aprovacao' ? 'AGUARDANDO APROVAÇÃO' :
                'EM REVISÃO (Minuta)'
              }</span>
              <span style={{ fontSize: '.75rem', opacity: 0.8 }}>v{activePetitionVersion}</span>
            </div>

            <div style={{ padding: '.875rem 1.375rem', borderBottom: '1px solid var(--border-light)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '.5rem' }}>
              <span className="card-title">Versão final da petição</span>
              <div style={{ display: 'flex', gap: '.375rem' }}>
                <button className="btn btn-outline btn-sm" onClick={() => handleDownload('docx')}>
                  <Download size={13} /> .docx
                </button>
                <button className="btn btn-outline btn-sm" onClick={() => handleDownload('md')}>
                  <Download size={13} /> .md
                </button>
              </div>
            </div>
            {loadingPetition ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
                <div className="spinner" style={{ width: 28, height: 28 }} />
              </div>
            ) : petition?.content_json ? (
              <div style={{ padding: '1rem', maxHeight: 520, overflowY: 'auto' }}>
                <DocumentPreview content={petition.content_json} />
              </div>
            ) : (
              <div style={{ padding: '2rem 1.375rem', textAlign: 'center', color: 'var(--text-muted)' }}>
                <FileText size={28} style={{ opacity: .3, marginBottom: '.75rem' }} />
                <p style={{ fontSize: '.85rem' }}>
                  Baixe o .docx para conferir a versão final antes de aprovar.
                </p>
              </div>
            )}
          </div>

          <CurationPanel petitionId={activePetitionId} />
        </div>

        {/* Painel lateral */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {/* Informações do caso */}
          <div className="card">
            <div className="card-header">
              <FileText size={15} style={{ color: 'var(--gold)' }} />
              <span className="card-title">Informações do caso</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '.5rem' }}>
              <InfoRow icon={<FileText size={12} />} label="Cliente" value={caso.client_name} />
              <InfoRow icon={<Building2 size={12} />} label="Reclamado" value={caso.defendant_name} />
              <InfoRow icon={<MapPin size={12} />} label="Comarca" value={caso.comarca} />
              {caso.hire_date && (
                <InfoRow label="Admissão" value={formatDate(caso.hire_date)} />
              )}
              {caso.termination_date && (
                <InfoRow label="Demissão" value={formatDate(caso.termination_date)} />
              )}
              <InfoRow label="Atendente" value={caso.atendente_name} />
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: '.375rem', borderTop: '1px solid var(--border-light)' }}>
                <span style={{ fontSize: '.775rem', color: 'var(--text-muted)' }}>Versão</span>
                <span className="badge badge-gold">
                  v{activePetitionVersion}
                </span>
              </div>
            </div>
          </div>

          {/* Quality gate da versão gerada pela IA (revisões manuais não têm relatório) */}
          <QualityPanel report={petition?.quality_report} />
          <CalculoPanel caseId={caso.case_id} />

          {/* Observações */}
          <div className="card">
            <div className="card-header">
              <FileText size={15} style={{ color: 'var(--gold)' }} />
              <span className="card-title">Observações finais</span>
              <span className="opt" style={{ marginLeft: 'auto' }}>opcional</span>
            </div>
            <textarea
              value={observations}
              onChange={e => setObservations(e.target.value)}
              rows={3}
              placeholder="Alguma observação para o protocolo?"
              style={{ resize: 'vertical' }}
            />
          </div>

          {/* Histórico de Versões */}
          <div className="card">
            <div className="card-header">
              <Clock size={15} style={{ color: 'var(--gold)' }} />
              <span className="card-title">Histórico de Versões</span>
            </div>
            {versions.length === 0 ? (
              <p style={{ fontSize: '.775rem', color: 'var(--text-muted)' }}>Nenhuma outra versão disponível.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '.4rem', maxHeight: 200, overflowY: 'auto' }}>
                {versions.map(v => (
                  <div key={v.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '.775rem', padding: '.35rem 0', borderBottom: '1px solid var(--border-light)' }}>
                    <span>v{v.version} ({v.status === 'aprovada' ? 'Aprovada' : v.status === 'aguardando_aprovacao' ? 'Aprov. pendente' : 'Em revisão'})</span>
                    <div style={{ display: 'flex', gap: '.25rem' }}>
                      <button className="btn btn-ghost btn-sm" style={{ padding: '0 .25rem', height: 'auto', fontSize: '.725rem' }} onClick={() => handleDownloadVersion(v.id, v.version, 'docx')}>
                        .docx
                      </button>
                      <button className="btn btn-ghost btn-sm" style={{ padding: '0 .25rem', height: 'auto', fontSize: '.725rem' }} onClick={() => handleDownloadVersion(v.id, v.version, 'md')}>
                        .md
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Ações */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '.5rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '.45rem', fontSize: '.8rem', cursor: 'pointer', padding: '.25rem 0', color: 'var(--text-primary)' }}>
              <input
                type="checkbox"
                checked={conferido}
                onChange={e => setConferido(e.target.checked)}
              />
              <span>Conferi o documento final</span>
            </label>
            <button
              className="btn btn-primary"
              style={{ background: 'var(--success)', borderColor: 'var(--success)' }}
              onClick={() => setShowConfirmModal(true)}
              disabled={!activePetitionId || approving || !conferido}
            >
              <CheckCircle size={14} /> Aprovar e Enviar para Protocolo
            </button>
            <button className="btn btn-outline" onClick={() => setShowAdjustModal(true)} disabled={!activePetitionId}>
              <AlertTriangle size={14} /> Solicitar Ajuste de Padronização
            </button>
          </div>
        </div>
      </div>

      {showConfirmModal && (
        <ConfirmApprovalModal
          caso={caso}
          onClose={() => setShowConfirmModal(false)}
          onConfirm={handleApprove}
          submitting={approving}
        />
      )}

      {showAdjustModal && (
        <AdjustModal
          onClose={() => setShowAdjustModal(false)}
          onConfirm={handleAdjustConfirm}
          submitting={adjusting}
        />
      )}
    </div>
  );
}

function FG({ label, hint, children, style }) {
  return (
    <div className="form-group" style={style}>
      <label style={{ fontSize: '.78rem', fontWeight: 500, display: 'block', marginBottom: '.25rem' }}>{label}</label>
      {children}
      {hint && <span className="field-hint" style={{ fontSize: '.72rem', color: 'var(--text-muted)', display: 'block', marginTop: '.125rem' }}>{hint}</span>}
    </div>
  );
}

function CurationPanel({ petitionId }) {
  const { user } = useAuthStore();
  const hasReviewPermission = user?.permissions?.includes(PERMISSIONS.PETITION_REVIEW);

  const [editingId, setEditingId] = useState(null);
  const [editedText, setEditedText] = useState('');
  const [editedScope, setEditedScope] = useState('');
  const [editedSection, setEditedSection] = useState('');

  // A extração roda em background depois do upload da revisão: sem o refetch, quem já estava com
  // a tela aberta via "nenhuma sugestão" para sempre e concluía que a IA não aprendeu nada.
  const { data: learnings = [], isLoading, refetch } = useQuery({
    queryKey: ['curation-learnings', petitionId],
    queryFn: () => petitionsApi.getLearnings({ petition_id: petitionId }),
    enabled: !!petitionId && hasReviewPermission,
    refetchInterval: query => (query.state.data?.length ? false : 15000),
  });

  if (!hasReviewPermission) return null;

  async function handleAccept(id, learning) {
    try {
      const payload = {
        instrucao_editada: id === editingId ? editedText : learning.instrucao_editada || learning.observacao,
        escopo: id === editingId ? editedScope : learning.escopo,
        secao: id === editingId ? editedSection : learning.secao,
      };
      await petitionsApi.acceptLearning(id, payload);
      toast.success('Aprendizado aceito!');
      setEditingId(null);
      refetch();
    } catch {
      toast.error('Erro ao aceitar aprendizado.');
    }
  }

  async function handleReject(id) {
    try {
      await petitionsApi.rejectLearning(id);
      toast.success('Aprendizado descartado.');
      refetch();
    } catch {
      toast.error('Erro ao descartar aprendizado.');
    }
  }

  async function handleDeactivate(id) {
    try {
      await petitionsApi.deactivateLearning(id);
      toast.success('Aprendizado desativado.');
      refetch();
    } catch {
      toast.error('Erro ao desativar aprendizado.');
    }
  }

  function startEdit(learning) {
    setEditingId(learning.id);
    setEditedText(learning.instrucao_editada || learning.observacao);
    setEditedScope(learning.escopo || 'geral');
    setEditedSection(learning.secao || 'geral');
  }

  return (
    <div className="card" style={{ border: '1px solid var(--border-light)', background: '#fff', padding: '1rem 1.375rem' }}>
      <div style={{ borderBottom: '1px solid var(--border-light)', paddingBottom: '.5rem', display: 'flex', alignItems: 'center', gap: '.5rem', marginBottom: '.75rem' }}>
        <Cpu size={16} color="var(--gold)" />
        <span style={{ fontSize: '.88rem', fontWeight: 700, color: 'var(--text-primary)' }}>Sugestões de Aprendizado de IA</span>
      </div>
      {!learnings.length && (
        <p style={{ fontSize: '.78rem', color: 'var(--text-muted)', margin: 0 }}>
          {isLoading
            ? 'Carregando sugestões...'
            : 'A IA ainda não devolveu sugestões desta revisão. A extração roda em segundo plano '
              + 'logo após o upload e esta lista se atualiza sozinha.'}
        </p>
      )}
      <div style={{ display: learnings.length ? 'flex' : 'none', flexDirection: 'column', gap: '.75rem' }}>
        <p style={{ fontSize: '.78rem', color: 'var(--text-muted)', margin: 0 }}>
          A IA identificou regras a partir das edições e notas de revisão. Aprove ou descarte-as para calibrar gerações futuras:
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '.75rem' }}>
          {learnings.map(l => {
            const isEditing = editingId === l.id;
            const statusColors = {
              pendente: { bg: '#fff7e6', color: '#b06000', label: 'Sugestão da IA' },
              aceito: { bg: '#e6f4ea', color: '#137333', label: 'Aceito' },
              descartado: { bg: '#fde8e8', color: '#c0392b', label: 'Descartado' },
              desativado: { bg: 'var(--bg-hover)', color: 'var(--text-muted)', label: 'Desativado' },
            }[l.status] || { bg: '#fff7e6', color: '#b06000', label: l.status };

            return (
              <div key={l.id} style={{ padding: '.875rem', border: '1px solid var(--border-light)', borderRadius: 'var(--radius-md)', background: l.status === 'pendente' ? 'rgba(255, 247, 230, 0.4)' : '#fff' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '.5rem', flexWrap: 'wrap', gap: '.5rem' }}>
                  <div style={{ display: 'flex', gap: '.35rem', alignItems: 'center' }}>
                    <span className="badge" style={{ background: statusColors.bg, color: statusColors.color, fontWeight: 600, border: 'none', borderRadius: '4px', padding: '.15rem .35rem', fontSize: '.68rem' }}>
                      {statusColors.label}
                    </span>
                    {l.alcance && (
                      <span className="badge" style={{ fontSize: '.7rem', background: l.alcance === 'caso' ? '#e8f0fe' : '#fce8e6', color: l.alcance === 'caso' ? '#1a73e8' : '#c5221f', border: 'none', borderRadius: '4px', padding: '.15rem .35rem', fontWeight: 600 }}>
                        {l.alcance === 'caso' ? 'Alcance: Caso' : 'Alcance: Escritório'}
                      </span>
                    )}
                    <span className="badge" style={{ fontSize: '.7rem', background: 'var(--bg-hover)', color: 'var(--text-secondary)', border: 'none', borderRadius: '4px', padding: '.15rem .35rem' }}>
                      Seção: {l.secao || 'Geral'}
                    </span>
                    <span className="badge" style={{ fontSize: '.7rem', background: 'var(--bg-hover)', color: 'var(--text-secondary)', border: 'none', borderRadius: '4px', padding: '.15rem .35rem' }}>
                      Escopo: {l.escopo || 'Geral'}
                    </span>
                  </div>
                  <span style={{ fontSize: '.7rem', color: 'var(--text-muted)' }}>
                    Origem: {l.origem === 'tag' ? 'Tag no Documento' : l.origem === 'ia' ? 'IA' : 'Humano'}
                  </span>
                </div>

                {l.ancora && (
                  <div style={{ fontSize: '.75rem', color: 'var(--text-secondary)', background: 'var(--bg-hover)', padding: '.4rem .6rem', borderRadius: 'var(--radius-sm)', marginBottom: '.5rem', borderLeft: '3px solid var(--border)' }}>
                    <strong>Trecho de referência:</strong> <em>"{l.ancora}"</em>
                  </div>
                )}

                <div style={{ fontSize: '.8rem', color: 'var(--text-primary)', marginBottom: '.5rem' }}>
                  <strong>Motivo:</strong> {l.observacao}
                </div>

                {isEditing ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '.5rem', padding: '.75rem', background: 'var(--bg)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-light)' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.5rem' }}>
                      <FG label="Seção">
                        <input
                          type="text"
                          value={editedSection}
                          onChange={e => setEditedSection(e.target.value)}
                          style={{ fontSize: '.75rem', padding: '.2rem .4rem' }}
                        />
                      </FG>
                      <FG label="Escopo">
                        <input
                          type="text"
                          value={editedScope}
                          onChange={e => setEditedScope(e.target.value)}
                          style={{ fontSize: '.75rem', padding: '.2rem .4rem' }}
                        />
                      </FG>
                    </div>
                    <FG label="Instrução para a IA">
                      <textarea
                        value={editedText}
                        onChange={e => setEditedText(e.target.value)}
                        rows={2}
                        style={{ fontSize: '.75rem', padding: '.4rem', width: '100%', resize: 'vertical' }}
                      />
                    </FG>
                    <div style={{ display: 'flex', gap: '.25rem', justifyContent: 'flex-end', marginTop: '.25rem' }}>
                      <button className="btn btn-ghost btn-sm" style={{ fontSize: '.725rem', padding: '.2rem .4rem' }} onClick={() => setEditingId(null)}>
                        Cancelar
                      </button>
                      <button className="btn btn-gold btn-sm" style={{ fontSize: '.725rem', padding: '.2rem .4rem' }} onClick={() => handleAccept(l.id, l)}>
                        Salvar e Aceitar
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div style={{ fontSize: '.8rem', color: 'var(--text-primary)', background: 'rgba(200, 144, 42, 0.05)', padding: '.5rem .75rem', borderRadius: 'var(--radius-sm)', borderLeft: '3px solid var(--gold)', marginBottom: '.5rem' }}>
                      <strong>Instrução da IA:</strong> {l.instrucao_editada || l.observacao}
                    </div>

                    <div style={{ display: 'flex', gap: '.35rem', justifyContent: 'flex-end' }}>
                      {l.status === 'pendente' && (
                        <>
                          <button className="btn btn-outline btn-sm" style={{ fontSize: '.725rem', padding: '.2rem .4rem' }} onClick={() => startEdit(l)}>
                            Editar
                          </button>
                          <button className="btn btn-outline btn-sm" style={{ fontSize: '.725rem', padding: '.2rem .4rem', color: 'var(--text-muted)' }} onClick={() => handleReject(l.id)}>
                            Descartar
                          </button>
                          <button className="btn btn-gold btn-sm" style={{ fontSize: '.725rem', padding: '.2rem .4rem' }} onClick={() => handleAccept(l.id, l)}>
                            Aceitar
                          </button>
                        </>
                      )}
                      {l.status === 'aceito' && (
                        <>
                          <button className="btn btn-outline btn-sm" style={{ fontSize: '.725rem', padding: '.2rem .4rem' }} onClick={() => startEdit(l)}>
                            Editar
                          </button>
                          <button className="btn btn-outline btn-sm" style={{ fontSize: '.725rem', padding: '.2rem .4rem', color: 'var(--text-muted)' }} onClick={() => handleDeactivate(l.id)}>
                            Desativar
                          </button>
                        </>
                      )}
                      {(l.status === 'descartado' || l.status === 'desativado') && (
                        <button className="btn btn-outline btn-sm" style={{ fontSize: '.725rem', padding: '.2rem .4rem' }} onClick={() => startEdit(l)}>
                          Reativar / Editar
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function InfoRow({ icon, label, value }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '.5rem' }}>
      <span style={{ fontSize: '.775rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '.25rem', flexShrink: 0 }}>
        {icon} {label}
      </span>
      <span style={{ fontSize: '.775rem', color: 'var(--text-primary)', fontWeight: 500, textAlign: 'right', wordBreak: 'break-word' }}>
        {value || '—'}
      </span>
    </div>
  );
}

export default function Aprovacao() {
  const { data: allCases = [], isLoading } = useQuery({
    queryKey: ['cases-approval'],
    queryFn: casesApi.list,
    refetchInterval: 30000,
  });
  const queryClient = useQueryClient();
  const location = useLocation();
  const [selectedCaseId, setSelectedCaseId] = useState(() => {
    return location.state?.caseId || null;
  });

  const cases = allCases.filter(c => APPROVAL_STATUSES.includes(c.status));
  const selectedCase = cases.find(c => c.case_id === selectedCaseId) || null;

  function handleResolved() {
    queryClient.invalidateQueries({ queryKey: ['cases-approval'] });
    setSelectedCaseId(null);
  }

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
        <div className="spinner" style={{ width: 28, height: 28 }} />
      </div>
    );
  }

  if (selectedCase) {
    return (
      <ApprovalMode
        caso={selectedCase}
        onBack={() => setSelectedCaseId(null)}
        onResolved={handleResolved}
      />
    );
  }

  return (
    <div>
      <div style={{ marginBottom: '1.5rem' }}>
        <h1>Aprovação Final</h1>
        <p style={{ marginTop: '.1rem' }}>
          {cases.length} {cases.length === 1 ? 'petição aguardando' : 'petições aguardando'} aprovação
        </p>
      </div>

      {cases.length === 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '40vh', gap: '.875rem', color: 'var(--text-muted)' }}>
          <CheckCircle size={44} style={{ opacity: .25 }} />
          <p>Nenhuma petição aguardando aprovação</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '.75rem' }}>
          {cases.map(c => (
            <div
              key={c.case_id}
              className="card"
              style={{ cursor: 'pointer' }}
              onClick={() => setSelectedCaseId(c.case_id)}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '.625rem', marginBottom: '.375rem' }}>
                    <span style={{ fontWeight: 600, fontSize: '1rem', color: 'var(--text-primary)' }}>
                      {c.client_name}
                    </span>
                    <span className="badge badge-info">Aguardando Aprovação</span>
                    <span className="badge badge-gold">v{c.petition_version}</span>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', fontSize: '.8rem', color: 'var(--text-muted)' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '.3rem' }}>
                      <Building2 size={12} /> {c.defendant_name}
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '.3rem' }}>
                      <MapPin size={12} /> {c.comarca}
                    </span>
                    <span>Atendente: {c.atendente_name}</span>
                  </div>
                </div>
                <button
                  className="btn btn-gold btn-sm"
                  style={{ marginLeft: '1.25rem', flexShrink: 0 }}
                  onClick={e => { e.stopPropagation(); setSelectedCaseId(c.case_id); }}
                >
                  <CheckCircle size={13} /> Aprovar
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
